import React from 'react'
import useSWR from 'swr'
import { fetcher } from '../../../swr-config'
import type { StandingsResponse, StandingRow } from '../types'

interface MATableTabProps {
  fixtureId: number
  homeTeamId: number
  awayTeamId: number
  leagueId: number
  leagueName?: string
  leagueLogo?: string | null
}

function FormDots({ form }: { form: string | null }) {
  if (!form) return null

  // Take last 5 characters
  const recent = form.slice(-5)

  return (
    <div className="standings-form">
      {recent.split('').map((char, i) => (
        <div
          key={i}
          className={`standings-form-dot ${char}`}
          title={char === 'W' ? 'Win' : char === 'D' ? 'Draw' : 'Loss'}
        >
          {char}
        </div>
      ))}
    </div>
  )
}

interface StandingsTableProps {
  standings: StandingRow[]
  homeTeamId: number
  awayTeamId: number
}

function StandingsTable({ standings, homeTeamId, awayTeamId }: StandingsTableProps) {
  const getGDClass = (gd: number) => {
    if (gd > 0) return 'gd-positive'
    if (gd < 0) return 'gd-negative'
    return 'gd-neutral'
  }

  return (
    <div className="standings-table-wrapper">
      <table className="standings-table">
        <thead>
          <tr>
            <th className="num">#</th>
            <th className="team-col">Team</th>
            <th className="num">PL</th>
            <th className="num">W</th>
            <th className="num">D</th>
            <th className="num">L</th>
            <th className="num hide-mobile">GF</th>
            <th className="num hide-mobile">GA</th>
            <th className="num">GD</th>
            <th className="num">Pts</th>
            <th>Form</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((row) => {
            const isHighlighted = row.team_id === homeTeamId || row.team_id === awayTeamId

            return (
              <tr key={row.team_id} className={isHighlighted ? 'highlighted' : ''}>
                <td className="num position-cell">{row.position}</td>
                <td>
                  <div className="standings-team-cell">
                    {row.team_crest && <img src={row.team_crest} alt="" />}
                    <span className="team-name">{row.team_name}</span>
                  </div>
                </td>
                <td className="num">{row.played}</td>
                <td className="num">{row.wins}</td>
                <td className="num">{row.draws}</td>
                <td className="num">{row.losses}</td>
                <td className="num hide-mobile">{row.goals_for}</td>
                <td className="num hide-mobile">{row.goals_against}</td>
                <td className={`num ${getGDClass(row.goal_difference)}`}>
                  {row.goal_difference > 0 ? '+' : ''}{row.goal_difference}
                </td>
                <td className="num points-cell">{row.points}</td>
                <td>
                  <FormDots form={row.form} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function MATableTab({ fixtureId, homeTeamId, awayTeamId, leagueId, leagueName, leagueLogo }: MATableTabProps) {
  const { data, error } = useSWR<StandingsResponse>(
    `/fixtures/${fixtureId}/standings`,
    fetcher,
    { revalidateOnFocus: false }
  )

  if (error) {
    return <div className="ma-error">Failed to load standings</div>
  }

  if (!data) {
    return <div className="ma-loading">Loading league table...</div>
  }

  return (
    <div className="standings-container">
      <div className="standings-header">
        {(data.league_logo || leagueLogo) && (
          <img src={data.league_logo || leagueLogo || ''} alt="" />
        )}
        <span className="league-name">{data.league_name || leagueName || 'League Table'}</span>
        {data.season && (
          <span className="season-badge">{data.season}</span>
        )}
      </div>

      <StandingsTable
        standings={data.standings}
        homeTeamId={homeTeamId}
        awayTeamId={awayTeamId}
      />
    </div>
  )
}
